import { ReportFinding, ReportValues, RunReportContext } from './types';
import { RunSnapshot, RunTraceRequest } from './runs';
import { ResolvedBinding } from './bindings';
import { Dimension } from './dimensions';
import { LayoutBlock } from './layout';
import { PlacedRun } from './placement';
import { ValuesSchema } from './schema';

/**
 * A block is one piece of a composed report: a readings table, a gallery, a
 * results matrix, a paragraph. Developers write block types, place them in a
 * report's layout and set their properties, which the block's schema
 * describes and normalizes.
 *
 * This is a block type's pure half: what it's called, its properties, the
 * checks it adds to the verdict, and whether it has anything to show for a
 * run. It runs wherever the report is judged, the server sandbox included.
 * The React half, which draws it, is registered by the same `type` in the
 * browser (src/frontend/reports/blocks).
 */

export type BlockGroup = 'Run data' | 'Results' | 'Text' | 'Record';

/** Everything a block reads when a report is judged or drawn. */
export interface BlockInput<P = ReportValues> {
  block: LayoutBlock;
  /** the block's properties, normalized by its schema */
  props: P;
  context: RunReportContext;
  run: RunSnapshot;
  /** the report's settings, normalized */
  settings: ReportValues;
  /** the report's per-run values */
  values: ReportValues;
  /** the run sorted into the legacy buckets, with the report's section map applied */
  placed: PlacedRun;
  /** the report's declared requirements, resolved against the run */
  bindings: Record<string, ResolvedBinding>;
  dimensions: Dimension[];
}

/** A per-run field a block asks the report's owner to fill in. */
export interface BlockField {
  key: string;
  label: string;
  optional: boolean;
  multiline?: boolean;
}

export interface BlockDefinition<P = ReportValues> {
  type: string;
  title: string;
  /** one line saying what the block shows */
  description: string;
  group: BlockGroup;
  props: ValuesSchema<P>;
  /** checks this block adds to the report's verdict */
  findings?: (input: BlockInput<P>) => ReportFinding[];
  /** nothing to show for this run: a block set to leave out when empty prints nothing */
  isEmpty?: (input: BlockInput<P>) => boolean;
  /** per-run fields it asks for, stored in the report's values under the block's id */
  fields?: (input: BlockInput<P>) => BlockField[];
  /**
   * point files it draws, read by the server into `context.traces` like a
   * definition's own `traces`. Asked with the report's settings as written
   * in code and its default values, before any trace has been read.
   */
  traces?: (input: BlockInput<P>) => RunTraceRequest[];
}

/** Declare a block type; typed for its own properties. */
export function defineBlock<P>(definition: BlockDefinition<P>): BlockDefinition<P> {
  return definition;
}

